import React, { useEffect, useState } from 'react';

import { OPEN_CONSENT_EVENT, readConsent, saveConsent } from '../utils/consent';

export default function CookieConsent() {
  const [decision, setDecision] = useState(() => readConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(() => decision?.analytics === true);

  useEffect(() => {
    const open = () => {
      const current = readConsent();
      setAnalytics(current?.analytics === true);
      setSettingsOpen(true);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, open);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, open);
  }, []);

  const choose = (analyticsChoice) => {
    const next = saveConsent({ analytics: analyticsChoice });
    setDecision(next);
    setAnalytics(next.analytics);
    setSettingsOpen(false);
  };

  if (decision && !settingsOpen) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[100] print:hidden sm:inset-x-auto sm:bottom-5 sm:left-5 sm:max-w-[40rem]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
    >
      <div className="rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-[0_18px_55px_rgba(15,23,42,0.2)] backdrop-blur sm:p-5">
        <h2 id="cookie-consent-title" className="text-lg font-semibold text-slate-950">
          Your privacy choices
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700">
          AffinityAI uses necessary browser technologies for sign-in, security and saved work.
          Optional analytics helps us understand use of approved public pages and starts only if
          you accept it. You can make or change this choice at any time.{' '}
          <a className="font-semibold underline" href="/cookies">
            Cookie Notice
          </a>
        </p>
        {settingsOpen ? (
          <div className="mt-4 rounded-xl border border-slate-200 p-4">
            <label className="flex items-start justify-between gap-4">
              <span>
                <strong>Strictly necessary</strong>
                <span className="block text-sm text-slate-600">
                  Required for authentication, security and core product state.
                </span>
              </span>
              <input
                type="checkbox"
                checked
                disabled
                aria-label="Strictly necessary technologies always enabled"
              />
            </label>
            <label className="mt-4 flex items-start justify-between gap-4">
              <span>
                <strong>Analytics</strong>
                <span className="block text-sm text-slate-600">
                  Google Analytics 4 measures approved public pages and limited public interactions
                  only after you consent.
                </span>
              </span>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(event) => setAnalytics(event.target.checked)}
              />
            </label>
          </div>
        ) : null}
        <div className="mt-4 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
          <button
            type="button"
            onClick={() => choose(true)}
            className="min-h-11 rounded-full border border-slate-900 bg-slate-900 px-5 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={() => choose(false)}
            className="min-h-11 rounded-full border border-slate-900 bg-white px-5 text-sm font-semibold text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
          >
            Reject non-essential
          </button>
          {settingsOpen ? (
            <button
              type="button"
              onClick={() => choose(analytics)}
              className="min-h-11 rounded-full border border-slate-900 bg-white px-5 text-sm font-semibold text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
            >
              Save settings
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="min-h-11 rounded-full border border-slate-900 bg-white px-5 text-sm font-semibold text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
            >
              Manage settings
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
